import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@mantine/dates';
import { Calendar } from 'tabler-icons-react';

import { FiltersModal, FiltersModalProps, StyledAutocomplete } from 'components/shared';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useForm } from 'hooks';
import {
   CommercialNetworkDetailsDTO,
   Option,
   TransferActionsOverviewFiltersForm,
} from 'interfaces';
import { HTTPService } from 'service';
import { errorHandler } from 'utils/errorHandler';
import { sortOptions } from 'utils/functions/sortOptions';
import {
   parseCommercialNetworkDetailsDTOIntoOption,
   parseDistributionCenterDTOIntoOption,
} from 'utils/parsers';
import { transferActionsOverviewFiltersFormValidation } from 'utils/validation';

import styles from './ClientPalletsTransferHistoryFiltersModal.module.css';

type ClientPalletsTransferHistoryFiltersFormData = Omit<
   TransferActionsOverviewFiltersForm,
   'client' | 'clientAddress' | 'storage'
>;

type ClientPalletsTransferHistoryFiltersModalProps = Pick<
   FiltersModalProps,
   'opened' | 'onClose'
> & {
   initialFormData: ClientPalletsTransferHistoryFiltersFormData;
   onSubmit: (filters: ClientPalletsTransferHistoryFiltersFormData) => void;
};

const ClientPalletsTransferHistoryFiltersModal = ({
   opened,
   initialFormData,
   onSubmit,
   onClose,
}: ClientPalletsTransferHistoryFiltersModalProps) => {
   const { t, i18n } = useTranslation();

   const [commercialNetworksData, setCommercialNetworksData] = useState<
      CommercialNetworkDetailsDTO[]
   >([]);
   const [isOptionsDataLoading, setIsOptionsDataLoading] = useState(false);
   const [isOptionsDataError, setIsOptionsDataError] = useState<boolean>(false);

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const {
      values: filterValues,
      errorsList,
      touched,
      setValues: setFilterValues,
      selectChangeHandler: selectFilterChangeHandler,
      submitHandler,
   } = useForm({
      initialState: initialFormData,
      validations: transferActionsOverviewFiltersFormValidation,
      onSubmit: () => {
         onSubmit(filterValues as ClientPalletsTransferHistoryFiltersFormData);
      },
   });

   useEffect(() => {
      setFilterValues(initialFormData);
   }, [initialFormData, setFilterValues]);

   const {
      commercialNetwork: selectedCommercialNetwork,
      distributionCenter: selectedDistributionCenter,
      dateFrom: selectedDateFrom,
      dateTo: selectedDateTo,
   } = filterValues as ClientPalletsTransferHistoryFiltersFormData;

   const handleChangeCommercialNetwork = (newCommercialNetwork: Option) => {
      setFilterValues(prevFilterValues => ({
         ...prevFilterValues,
         commercialNetwork: newCommercialNetwork,
         distributionCenter: undefined,
      }));
   };

   const handleClearCommercialNetwork = () => {
      setFilterValues(prevFilterValues => ({
         ...prevFilterValues,
         commercialNetwork: undefined,
         distributionCenter: undefined,
      }));
   };

   const handleChangeDistributionCenter = (newDistributionCenter: Option) => {
      setFilterValues(prevFilterValues => ({
         ...prevFilterValues,
         distributionCenter: newDistributionCenter,
      }));
   };

   const handleChangeDateFrom = (newDate: Date | null) => {
      setFilterValues(prevFilterValues => ({
         ...prevFilterValues,
         dateFrom: newDate,
      }));
   };

   const handleChangeDateTo = (newDate: Date | null) => {
      setFilterValues(prevFilterValues => ({
         ...prevFilterValues,
         dateTo: newDate,
      }));
   };

   const handleOptionsDataError = (error: unknown) =>
      errorHandler(error, () => setIsOptionsDataError(true));

   const handleFetchFiltersData = useCallback(() => {
      setIsOptionsDataLoading(true);
      HTTPService.getUserCommercialNetworks()
         .then(getNetworksResponse => {
            setCommercialNetworksData(getNetworksResponse.data.commercialNetworks);
         })
         .catch(handleOptionsDataError)
         .finally(() => setIsOptionsDataLoading(false));
   }, []);

   useEffect(() => {
      handleFetchFiltersData();
   }, [handleFetchFiltersData]);

   const commercialNetworkOptions = useMemo(() => {
      const parsedCommercialNetworkOptions = commercialNetworksData.map(
         parseCommercialNetworkDetailsDTOIntoOption,
      );
      return sortOptions(parsedCommercialNetworkOptions);
   }, [commercialNetworksData]);

   const distributionCenterOptions = useMemo(() => {
      const selectedCommercialNetworkAddresses =
         commercialNetworksData.find(
            commercialNetwork => commercialNetwork.id === selectedCommercialNetwork?.value,
         )?.addresses || [];
      return selectedCommercialNetworkAddresses.map(parseDistributionCenterDTOIntoOption);
   }, [commercialNetworksData, selectedCommercialNetwork?.value]);

   return (
      <FiltersModal
         opened={opened}
         isLoading={isOptionsDataLoading}
         error={isOptionsDataError ? DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA : undefined}
         onSubmit={submitHandler}
         onClose={onClose}
      >
         <FiltersModal.GroupContainer>
            <StyledAutocomplete
               label={t('clientPalletsTransferHistoryFiltersModal.selectNetwork')}
               value={selectedCommercialNetwork}
               options={commercialNetworkOptions}
               classNames={{
                  container: styles.autocompleteContainer,
               }}
               onSelect={handleChangeCommercialNetwork}
               onClear={handleClearCommercialNetwork}
            />
            <StyledAutocomplete
               label={t('clientPalletsTransferHistoryFiltersModal.selectDistributionCenter')}
               value={selectedDistributionCenter}
               disabled={!selectedCommercialNetwork}
               options={distributionCenterOptions}
               classNames={{
                  container: styles.autocompleteContainer,
               }}
               onSelect={handleChangeDistributionCenter}
               onClear={() => selectFilterChangeHandler(undefined, 'distributionCenter')}
            />
         </FiltersModal.GroupContainer>
         <FiltersModal.CombinedRow>
            <DatePicker
               label={t('clientPalletsTransferHistoryFiltersModal.dateFrom')}
               value={selectedDateFrom}
               inputFormat="DD/MM/YYYY"
               icon={<Calendar color="#3E3E4B" size="20" />}
               locale={i18n.language}
               error={touched.dateFrom && errorsList.dateFrom}
               onChange={handleChangeDateFrom}
            />
            <DatePicker
               label={t('clientPalletsTransferHistoryFiltersModal.dateTo')}
               value={selectedDateTo}
               inputFormat="DD/MM/YYYY"
               icon={<Calendar color="#3E3E4B" size="20" />}
               locale={i18n.language}
               error={touched.dateTo && errorsList.dateTo}
               onChange={handleChangeDateTo}
            />
         </FiltersModal.CombinedRow>
      </FiltersModal>
   );
};

export default ClientPalletsTransferHistoryFiltersModal;
